import { useCallback, useMemo } from 'react';
import type { UpdateVendorInput } from '@peles/shared';
import { useVendor, useUpdateVendor } from '../../hooks/useVendors';
import { QuickEditModal, type QuickEditField } from '../common/QuickEditModal';

const FIELDS: QuickEditField[] = [
  { key: 'businessName', label: 'Business Name', required: true },
  { key: 'contactName', label: 'Contact Name' },
  { key: 'phone', label: 'Phone', type: 'tel' },
  { key: 'email', label: 'Email', type: 'email' },
  { key: 'website', label: 'Website' },
  { key: 'notes', label: 'Notes', type: 'textarea' },
];

const OPTIONAL_KEYS = ['contactName', 'phone', 'email', 'website', 'notes'] as const;

export interface VendorQuickEditModalProps {
  vendorId: string | null;
  open: boolean;
  onClose: () => void;
}

/**
 * Quick view/edit of a vendor's key details, opened whenever a vendor is
 * linked somewhere (expense line, event vendor list, ...).
 */
export function VendorQuickEditModal({ vendorId, open, onClose }: VendorQuickEditModalProps) {
  const { data: vendor, isLoading } = useVendor(open ? (vendorId ?? '') : '');
  const updateVendor = useUpdateVendor();

  const initialValues = useMemo(
    () => ({
      businessName: vendor?.businessName ?? '',
      contactName: vendor?.contactName ?? '',
      phone: vendor?.phone ?? '',
      email: vendor?.email ?? '',
      website: vendor?.website ?? '',
      notes: vendor?.notes ?? '',
    }),
    [vendor],
  );

  const subtitle = useMemo(() => {
    const categories = vendor?.categories.map((c) => c.categoryName) ?? [];
    return categories.length > 0 ? categories.join(' · ') : undefined;
  }, [vendor]);

  const handleSave = useCallback(
    async (values: Record<string, string>) => {
      if (!vendorId) return;
      const updates: UpdateVendorInput = {};
      if (values.businessName.trim() !== initialValues.businessName) {
        updates.businessName = values.businessName.trim();
      }
      for (const key of OPTIONAL_KEYS) {
        if ((values[key] ?? '') !== initialValues[key]) {
          updates[key] = values[key].trim() || null;
        }
      }
      if (Object.keys(updates).length === 0) return;
      await updateVendor.mutateAsync({ id: vendorId, updates });
    },
    [vendorId, initialValues, updateVendor],
  );

  return (
    <QuickEditModal
      open={open && !!vendorId}
      onClose={onClose}
      title="Vendor Details"
      subtitle={subtitle}
      fields={FIELDS}
      initialValues={initialValues}
      onSave={handleSave}
      saving={updateVendor.isPending}
      loading={isLoading}
    />
  );
}
